import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";

export default function GovOpportunityNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    solicitationNumber: "", title: "", agency: "", agencyType: "FEDERAL",
    naicsCode: "", pscCode: "", setAsideType: "", contractVehicle: "",
    estimatedValue: "", responseDueDate: "", role: "PRIME", samGovUrl: "",
  });

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const payload: any = {
        solicitationNumber: form.solicitationNumber,
        title: form.title,
        agency: form.agency,
        agencyType: form.agencyType,
        role: form.role,
      };
      if (form.naicsCode)        payload.naicsCode = form.naicsCode;
      if (form.pscCode)          payload.pscCode = form.pscCode;
      if (form.setAsideType)     payload.setAsideType = form.setAsideType;
      if (form.contractVehicle)  payload.contractVehicle = form.contractVehicle;
      if (form.estimatedValue)   payload.estimatedValue = Number(form.estimatedValue);
      if (form.responseDueDate)  payload.responseDueDate = form.responseDueDate;
      if (form.samGovUrl)        payload.samGovUrl = form.samGovUrl;
      await api.post("/gov-opportunities", payload);
      qc.invalidateQueries({ queryKey: ["/gov-opportunities"] });
      navigate("/gov-opportunities");
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Failed to create opportunity.");
    } finally { setBusy(false); }
  }

  return (
    <>
      <PageHeader title="New Government Opportunity" description="Manually add a SAM.gov or state/local solicitation." />
      <form onSubmit={submit} className="p-6">
        <Card className="max-w-2xl">
          <CardContent className="grid gap-4 p-6 md:grid-cols-2">
            <div>
              <Label htmlFor="solicitationNumber">Solicitation # *</Label>
              <Input id="solicitationNumber" required value={form.solicitationNumber} onChange={(e) => set("solicitationNumber", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="agencyType">Agency type</Label>
              <Select id="agencyType" value={form.agencyType} onChange={(e) => set("agencyType", e.target.value)}>
                <option value="FEDERAL">Federal</option>
                <option value="STATE">State</option>
                <option value="LOCAL">Local</option>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="title">Title *</Label>
              <Input id="title" required value={form.title} onChange={(e) => set("title", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="agency">Agency *</Label>
              <Input id="agency" required placeholder="DHS, GSA, DOD, …" value={form.agency} onChange={(e) => set("agency", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="role">CBC role</Label>
              <Select id="role" value={form.role} onChange={(e) => set("role", e.target.value)}>
                <option value="PRIME">Prime</option>
                <option value="SUB">Subcontractor</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="naicsCode">NAICS</Label>
              <Input id="naicsCode" placeholder="541511" value={form.naicsCode} onChange={(e) => set("naicsCode", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="pscCode">PSC</Label>
              <Input id="pscCode" value={form.pscCode} onChange={(e) => set("pscCode", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="setAsideType">Set-aside</Label>
              <Input id="setAsideType" placeholder="SDVOSB, 8(a), WOSB, …" value={form.setAsideType} onChange={(e) => set("setAsideType", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="contractVehicle">Vehicle</Label>
              <Input id="contractVehicle" placeholder="GSA MAS, CIO-SP3, …" value={form.contractVehicle} onChange={(e) => set("contractVehicle", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="estimatedValue">Estimated value ($)</Label>
              <Input id="estimatedValue" type="number" min="0" value={form.estimatedValue} onChange={(e) => set("estimatedValue", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="responseDueDate">Response due</Label>
              <Input id="responseDueDate" type="date" value={form.responseDueDate} onChange={(e) => set("responseDueDate", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="samGovUrl">SAM.gov URL</Label>
              <Input id="samGovUrl" type="url" value={form.samGovUrl} onChange={(e) => set("samGovUrl", e.target.value)} />
            </div>
          </CardContent>
          <div className="flex items-center justify-between border-t px-6 py-4">
            {error ? <p className="text-sm text-destructive">{error}</p> : <span />}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => navigate("/gov-opportunities")}>Cancel</Button>
              <Button type="submit" disabled={busy || !form.solicitationNumber || !form.title || !form.agency}>
                {busy ? "Saving…" : "Create"}
              </Button>
            </div>
          </div>
        </Card>
      </form>
    </>
  );
}
